import { View } from "@westpierstudio/luma/mvc";
import { getEase, Tween } from "@westpierstudio/luma/createjs";
import { GameServices } from "game/GameServices";
import { ReelSymbol } from "./ReelSymbol";
import { SpinConfig } from "./ReelsModel";
import { CoreSymbolID, CoreSymbolNumberMap, ReelSymbols, SymbolID } from "servertypes/SharedTypes";
import { mod } from "utils/Misc";
import { Signal } from "type-signals";
import { SymbolCache } from "./Reels";

const SYMBOL_COUNT = 4; //0 is offscreen, 1-3 are in view

enum State {
    STOP,
    MOVE,
    BOUNCE
}

class ReelSignals {
    bonusSymbolLand = new Signal<(reelIndex: number) => void>();
    scatterWinComplete = new Signal<(reelIndex: number) => void>();
    reelStop = new Signal<(reelIndex: number) => void>();
    reelStopSound = new Signal();
    topSymbolInteract = new Signal();
}

export class Reel extends View {
    private readonly defaultY: number;
    private readonly symbols: ReelSymbol[] = [];
    private reelStrip: SymbolID[] = [];
    private reelStripIndex = 0;
    private swappedSymbols: {index: number, symbol: SymbolID}[] = [];
    private spinConfig!: SpinConfig;
    private state = State.STOP;
    signals = new ReelSignals();

    constructor(services: GameServices, private readonly id: number, private readonly symbolHeight: number,
        symbolCache: SymbolCache) {
        super(services);

        let symbol;

        for (let i=0; i<SYMBOL_COUNT; i++) {

            symbol = new ReelSymbol(services, id === 0, symbolCache);
            symbol.y = i * this.symbolHeight;

            symbol.signals.scatterWinComplete.add(() => this.signals.scatterWinComplete.dispatch(this.id));
            symbol.signals.topSymbolInteract.add(() => this.signals.topSymbolInteract.dispatch());

            this.addChild(symbol);
            this.symbols.push(symbol);
        }

        this.defaultY = this.y;
    }

    get totalSpinTime(): number {

        const {anticipationActive, anticipationTime, baseSpinTime, reelStopDelay, reelStopTimeIn, reelStopTimeOut} = this.spinConfig;

        let spinTime = baseSpinTime + (this.id * reelStopDelay);
        const stopTime = reelStopTimeIn + reelStopTimeOut;

        if (anticipationActive) {
            spinTime += anticipationTime;
        }

        return spinTime + stopTime;
    }

    getVisibleSymbolGlobalPosition(symbolIndex: number) {
        const symbol = this.symbols[symbolIndex + 1];
        return this.toGlobal(symbol.position);
    }

    setSymbols(symbols: ReelSymbols, inBonus: boolean, multipliers?: Partial<CoreSymbolNumberMap>): void {

        let multiplier;
        const useWildBonus = this.useWildBonusSymbol(inBonus);

        for (const [index, symbolID] of symbols.entries()) {

            if (multipliers) {
                multiplier = multipliers[symbolID as CoreSymbolID];
            }

            this.symbols[index+1].setSymbol(symbolID, useWildBonus, multiplier);
        }
    }

    setReelStrip(reelStrip: SymbolID[]): void {
        this.reelStrip = reelStrip;
    }

    setSpinConfig(spinConfig: SpinConfig): void {
        this.spinConfig = spinConfig;
    }

    getRandomReelStripIndex(): number {
        return Math.floor(Math.random() * this.reelStrip.length);
    }

    swapReelStripSymbols(startIndex: number, symbols: SymbolID[]): void {

        //Restore the last swapped symbols prior to the new swap to prevent persistent changes
        this.restoreSwappedSymbols();

        let index = startIndex;

        for (const symbol of symbols) {

            this.swappedSymbols.push({index, symbol: this.reelStrip[index]});
            this.reelStrip[index] = symbol;

            index++;
            index = index % this.reelStrip.length;
        }
    }

    spin(stopIndex: number, inBonus: boolean, playLandAnim: boolean[], spinTimePadding=0,
        multipliers?: Partial<CoreSymbolNumberMap>): void {

        const {anticipationActive, anticipationTime, baseSpinTime, reelStopDelay, symbolsPerSecond} = this.spinConfig;

        let totalMoveTimeMS = baseSpinTime + (this.id * reelStopDelay) + spinTimePadding;

        if (anticipationActive) {
            totalMoveTimeMS += anticipationTime;
        }

        const totalDistance = (symbolsPerSecond * this.symbolHeight) * (totalMoveTimeMS/1000);
        const symbolMoveCount = Math.floor(totalDistance/this.symbolHeight);
        const oneSymbolMoveTimeMS = (symbolMoveCount > 0) ? totalMoveTimeMS/symbolMoveCount : 0;

        //We want the stopindex to correspond with the bottom symbol so inlcude a shift down of 3
        this.reelStripIndex = mod(stopIndex - symbolMoveCount + 3, this.reelStrip.length);

        this.setNextOffScreenSymbol(inBonus, multipliers);
        this.startMoveTween(oneSymbolMoveTimeMS, symbolMoveCount, inBonus, playLandAnim, multipliers);
    }

    slampStop(symbols: ReelSymbols, inBonus: boolean, playLandAnim: boolean[],
        multipliers?: Partial<CoreSymbolNumberMap>): void {

        if (this.state === State.MOVE) {

            Tween.removeTweens(this);
            this.y = this.defaultY;

            this.setSymbols(symbols, inBonus, multipliers);
            this.stopWithBounce(playLandAnim);
        }
    }

    startWinAnimations(animateState: boolean[]): void {

        for (const [i, animate] of animateState.entries()) {

            if (animate) {
                this.symbols[i+1].startWinAnimLoop();
            }
        }
    }

    startScatterWinAnimationSequence(row: number) {
        this.symbols[row+1].scatterWinSequence();
    }

    startScatterWinAnimationLoop(row: number) {
        this.symbols[row+1].scatterWinLoop();
    }

    stopWinAnimations(): void {

        for (const [i, symbol] of this.symbols.entries()) {

            if (i > 0) {
                symbol.stopWinAnimLoop();
            }
        }
    }

    setTopSymbolInteraction(interactive: boolean): void {

        let index = 0;

        for (const symbol of this.symbols) {

            const isInteractive = interactive && symbol.id === `M1` && index != 0;
            symbol.setInteractive(isInteractive);

            index++;
        }
    }

    override reset(): void {

        this.reelStrip = [];
        this.reelStripIndex = 0;
        this.swappedSymbols = [];

        Tween.removeTweens(this);
        this.y = this.defaultY;
        this.state = State.STOP;

        for (const symbol of this.symbols) {
            symbol.reset();
        }
    }

    private startMoveTween(oneSymbolMoveTimeMS: number, symbolMoveCount: number, inBonus: boolean,
        playLandAnim: boolean[], multipliers?: Partial<CoreSymbolNumberMap>): void {

        this.state = State.MOVE;
        const targetY = this.y + this.symbolHeight;
        const moveTween = Tween.get(this);

        while (symbolMoveCount > 0) {

            moveTween.to({y: targetY}, oneSymbolMoveTimeMS);
            moveTween.call(() => {
                this.setNextReelStripIndex();
                this.updateSymbolsInView(inBonus, multipliers);
                this.setNextOffScreenSymbol(inBonus, multipliers);
            });
            moveTween.to({y: this.defaultY}, 0);

            symbolMoveCount--;
        }

        moveTween.call(() => {
            this.signals.reelStopSound.dispatch();
            this.stopWithBounce(playLandAnim);
        });
    }

    private restoreSwappedSymbols(): void {

        if (this.swappedSymbols.length > 0) {

            for (const item of this.swappedSymbols) {
                this.reelStrip[item.index] = item.symbol;
            }

            this.swappedSymbols = [];
        }
    }

    private setNextReelStripIndex(): void {

        this.reelStripIndex++;
        this.reelStripIndex = this.reelStripIndex % this.reelStrip.length;
    }

    private setNextOffScreenSymbol(inBonus: boolean, multipliers?: Partial<CoreSymbolNumberMap>): void {

        const symbolID = this.reelStrip[this.reelStripIndex];

        let multiplier;

        if (multipliers) {
            multiplier = multipliers[symbolID as CoreSymbolID];
        }

        this.symbols[0].setSymbol(this.reelStrip[this.reelStripIndex], this.useWildBonusSymbol(inBonus), multiplier);
    }

    //Set each symbol id to the one preceding it
    private updateSymbolsInView(inBonus: boolean, multipliers?: Partial<CoreSymbolNumberMap>): void {

        let multiplier, symbolID;
        const useWildBonus = this.useWildBonusSymbol(inBonus);

        for (let i=SYMBOL_COUNT-1; i>0; i--) {

            symbolID = this.symbols[i-1].id;

            if (multipliers) {
                multiplier = multipliers[symbolID as CoreSymbolID];
            }

            this.symbols[i].setSymbol(symbolID, useWildBonus, multiplier);
        }
    }

    private stopWithBounce(playLandAnim: boolean[]): void {

        this.state = State.BOUNCE;

        const {reelStopDistance, reelStopEaseIn, reelStopEaseOut, reelStopTimeIn, reelStopTimeOut} = this.spinConfig;

        Tween.get(this)
        .to({y: this.y + reelStopDistance}, reelStopTimeIn, getEase(reelStopEaseIn))
        .to({y: this.y}, reelStopTimeOut, getEase(reelStopEaseOut))
        .call(() => {
            this.state = State.STOP;
            this.signals.reelStop.dispatch(this.id);
        });

        for (const [i,symbol] of this.symbols.entries()) {
            if (i !== 0) {

                if (playLandAnim[i-1]) {

                    symbol.playLandAnim();
                    this.signals.bonusSymbolLand.dispatch(this.id);
                }

                if (this.id === 0) {
                    symbol.playMultiBadgeLandAnim();
                }
            }
        }
    }

    private useWildBonusSymbol(inBonus: boolean): boolean {
        return (inBonus && (this.id === 1 || this.id === 2));
    }
}